import React, { ChangeEvent } from 'react';
import { CapacityWarningInfo } from './reservationForm.utils.ts';

export type TableOption = {
  id: string | number;
  name: string;
  floorId: string | number;
  minCovers: number;
  maxCovers: number;
};

export type ReservationFormCapacityFieldsProps = {
  time: string;
  guests: number | string;
  onGuestsChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showGuestLimits: boolean;
  minGuestsLimit?: number;
  maxGuestsLimit?: number;
  salaUsaMesas: boolean;
  guestValidationError: string;
  guestCapacityWarning: CapacityWarningInfo | null;
  guestCapacityWarningConfirmed: boolean;
  onGuestCapacityWarningConfirmedChange: (checked: boolean) => void;
  tableIds: string[];
  onTableChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  tablesForSala: TableOption[];
  reservedTableIds: Set<string>;
  tableAvailabilityMessage: string;
  tableAvailabilityStatus: 'available' | 'unavailable' | null;
};

export default function ReservationFormCapacityFields({
  time,
  guests,
  onGuestsChange,
  showGuestLimits,
  minGuestsLimit,
  maxGuestsLimit,
  salaUsaMesas,
  guestValidationError,
  guestCapacityWarning,
  guestCapacityWarningConfirmed,
  onGuestCapacityWarningConfirmedChange,
  tableIds,
  onTableChange,
  tablesForSala,
  reservedTableIds,
  tableAvailabilityMessage,
  tableAvailabilityStatus,
}: ReservationFormCapacityFieldsProps) {
  if (!time) return null;

  return (
    <>
      <div className="form-group">
        <label htmlFor="guests">
          Pessoas
          {showGuestLimits && (
            <span className="field-hint">
              ({minGuestsLimit ?? 1}-{maxGuestsLimit ?? 20} pessoas)
            </span>
          )}
        </label>
        <input
          id="guests"
          name="guests"
          type="number"
          min={1}
          max={maxGuestsLimit}
          value={guests}
          onChange={onGuestsChange}
          required
          className={guestValidationError ? 'has-error' : (guestCapacityWarning ? 'has-warning' : '')}
        />
        {guestValidationError && (
          <span className="field-error">{guestValidationError}</span>
        )}
        {guestCapacityWarning && (
          <div className="reservation-form-warning" role="alert">
            <strong className="reservation-form-warning-title">{guestCapacityWarning.title}</strong>
            <p className="reservation-form-warning-text">{guestCapacityWarning.message}</p>
            <label className="reservation-form-warning-check">
              <input
                type="checkbox"
                checked={guestCapacityWarningConfirmed}
                onChange={(event) => onGuestCapacityWarningConfirmedChange(event.target.checked)}
              />
              <span>{guestCapacityWarning.confirmLabel}</span>
            </label>
          </div>
        )}
      </div>

      {salaUsaMesas && (
        <div className="form-group">
          <label htmlFor="tableId">Mesas <span className="required">*</span></label>
          <select
            id="tableId"
            name="tableId"
            value={tableIds}
            onChange={onTableChange}
            multiple
            size={Math.min(6, Math.max(4, tablesForSala.length))}
            required
          >
            {tablesForSala.map((table) => {
              const isReserved = reservedTableIds.has(String(table.id));
              return (
                <option key={table.id} value={table.id}>
                  {table.name} ({table.minCovers}-{table.maxCovers} pessoas){isReserved ? ' (reservado)' : ''}
                </option>
              );
            })}
          </select>
          <span className="field-hint">Selecione uma ou mais mesas do mesmo ambiente.</span>
          {tableAvailabilityMessage && (
            <span className={`table-availability-message ${tableAvailabilityStatus ?? 'neutral'}`}>
              {tableAvailabilityMessage}
            </span>
          )}
        </div>
      )}
    </>
  );
}
